import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, MagnifyingGlass, X } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parse "23/02/2026", "Feb 23, 2026", "2026-02-23" etc → year string. Returns null if not parsable.
const parseYear = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  let m = s.match(/(\d{4})/);
  if (m) return m[1];
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return String(d.getFullYear());
  return null;
};

const parseDateForSort = (raw) => {
  if (!raw) return 0;
  const s = String(raw).trim();
  // dd/mm/yyyy
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])).getTime();
  // yyyy-mm-dd
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const ArticleCardSkeleton = () => (
  <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden animate-pulse">
    <div className="aspect-[16/10] bg-neutral-100" />
    <div className="p-6 space-y-3">
      <div className="h-3 w-1/3 bg-neutral-100 rounded" />
      <div className="h-5 w-4/5 bg-neutral-100 rounded" />
      <div className="h-4 w-full bg-neutral-100 rounded" />
      <div className="h-4 w-2/3 bg-neutral-100 rounded" />
    </div>
  </div>
);

export const NewsListing = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeYear, setActiveYear] = useState('all');

  useEffect(() => {
    axios.get(`${API}/news`)
      .then(r => setArticles(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Sort newest-first then derive year buckets.
  const sorted = useMemo(() => [...articles].sort((a, b) => parseDateForSort(b.date) - parseDateForSort(a.date)), [articles]);

  const years = useMemo(() => {
    const set = new Set();
    sorted.forEach(a => { const y = parseYear(a.date); if (y) set.add(y); });
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [sorted]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sorted.filter(a => {
      if (activeYear !== 'all' && parseYear(a.date) !== activeYear) return false;
      if (!q) return true;
      return `${a.title || ''} ${a.excerpt || ''} ${a.content || ''} ${a.author || ''}`.toLowerCase().includes(q);
    });
  }, [sorted, search, activeYear]);

  return (
    <section className="py-16 lg:py-24 bg-white" data-testid="news-listing">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Search + year tabs */}
        <div className="mb-10 flex flex-col gap-4">
          <div className="relative max-w-xl">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" weight="bold" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search news, e.g. iESE, recycling, training..."
              className="w-full pl-10 pr-10 py-2.5 rounded-full bg-neutral-50 border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-lime-500/40 focus:border-lime-400 text-neutral-800 placeholder-neutral-400 transition-all"
              data-testid="news-search-input"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 p-0.5 rounded-full"
                aria-label="Clear search"
                data-testid="news-search-clear"
              >
                <X size={14} weight="bold" />
              </button>
            )}
          </div>

          {years.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="news-year-tabs">
              <button
                type="button"
                onClick={() => setActiveYear('all')}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${activeYear === 'all' ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-700 border-neutral-200 hover:border-lime-400 hover:text-lime-600'}`}
                data-testid="news-year-all"
              >
                All
              </button>
              {years.map(y => (
                <button
                  key={y}
                  type="button"
                  onClick={() => setActiveYear(y)}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${activeYear === y ? 'bg-lime-600 text-white border-lime-600' : 'bg-white text-neutral-700 border-neutral-200 hover:border-lime-400 hover:text-lime-600'}`}
                  data-testid={`news-year-${y}`}
                >
                  {y}
                </button>
              ))}
            </div>
          )}

          {!loading && (
            <p className="text-sm text-neutral-500" data-testid="news-result-count">
              {filtered.length === sorted.length ? `${sorted.length} articles` : `${filtered.length} of ${sorted.length} articles`}
              {activeYear !== 'all' && ` from ${activeYear}`}
              {search && ` matching "${search}"`}
            </p>
          )}
        </div>

        {/* Article grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[0, 1, 2].map(i => <ArticleCardSkeleton key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-neutral-200 rounded-2xl" data-testid="news-empty-state">
            <p className="text-neutral-500 mb-2">No news articles match your search.</p>
            {(search || activeYear !== 'all') && (
              <button
                onClick={() => { setSearch(''); setActiveYear('all'); }}
                className="text-lime-600 hover:text-lime-700 text-sm font-medium"
                data-testid="news-reset-filters"
              >
                Clear search &amp; filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.4) }}
              >
                <Link
                  to={`/news/${article.slug}`}
                  className="group block bg-white rounded-2xl border border-neutral-200 overflow-hidden hover:shadow-lg hover:border-lime-200 transition-all duration-300 h-full"
                  data-testid={`news-card-${article.slug}`}
                >
                  {article.image_url && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    {article.date && (
                      <div className="flex items-center gap-1.5 text-xs text-neutral-500 mb-3">
                        <Calendar size={14} weight="duotone" className="text-lime-600" />
                        <span>{article.date}</span>
                      </div>
                    )}
                    <h3 className="font-outfit font-semibold text-lg text-neutral-900 group-hover:text-lime-600 transition-colors mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-neutral-600 text-sm line-clamp-3 mb-4">{article.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-lime-600 text-sm font-medium group-hover:gap-2 transition-all">
                      Read More <ArrowRight size={14} weight="bold" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
